import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Bell, Shield, CreditCard, UserCircle, AtSign, Phone, Save,
  Loader2, Key, Hash, Copy, RefreshCw, Layers, MessageSquare, MessageCircle,
  Smartphone, Mail, BarChart3, Wallet, Calendar, CheckCircle, AlertCircle,
  X, Plus, RotateCcw, ShieldCheck, ChevronRight, Clock, KeyRound, Check,
  ShieldAlert, FileBarChart, PlusCircle, Monitor, Code, Radio, Globe,
  Trash2, History, Receipt, BellRing, AlertTriangle, Send, User, Lock
} from 'lucide-react';
import api, { userAlertAPI } from '../../services/api';

const TABS = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

const inputClass = "w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 placeholder-gray-400";

const Toggle = ({ enabled, onChange, disabled }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!enabled); }}
    className="relative inline-flex items-center transition-all duration-300 flex-shrink-0"
    style={{ width: 44, height: 24, borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      background: enabled ? 'linear-gradient(135deg, #4f46e5, #06b6d4)' : '#cbd5e1', boxShadow: enabled ? '0 0 12px rgba(99,102,241,0.3)' : 'inset 0 1px 3px rgba(0,0,0,0.1)' }}>
    <div className="absolute rounded-full bg-white transition-all duration-300" style={{ width: 18, height: 18, top: 3, left: enabled ? 23 : 3, boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }} />
  </button>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 ${className}`}>{children}</div>
);

const CardHeader = ({ icon: Icon, title, subtitle, badge }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="p-2 rounded-lg bg-indigo-50">
      <Icon className="w-4 h-4 text-indigo-500" />
    </div>
    <div className="flex-1">
      <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {badge && <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600">{badge}</span>}
  </div>
);

const Label = ({ children }) => <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{children}</label>;

const GlobalSettingsPage = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  // Profile state
  const [profile, setProfile] = useState({ full_name: '', email: '', company_name: '', username: '', role: '', balance: 0, api_key: '', created_at: '', mobile: '' });

  // Alert config state
  const [alertConfig, setAlertConfig] = useState({
    low_balance_enabled: false, low_balance_threshold: 1000,
    login_alert_enabled: false, sms_summary_enabled: false,
    sms_summary_frequency: 'daily', email_enabled: false, sms_enabled: false,
  });

  // Alert contacts state
  const [alertEmails, setAlertEmails] = useState([{ value: '', verified: false }]);
  const [alertMobiles, setAlertMobiles] = useState([{ value: '', verified: false }]);
  const [savingContacts, setSavingContacts] = useState(false);

  // Legacy OTP state (kept for backward compat with deprecated renderContact)
  const [twoLayerState, setTwoLayerState] = useState({});

  // Alert Recipient Requests (Master Admin Approval Workflow)
  const [recipientRequests, setRecipientRequests] = useState([]);
  const [newRecipientType, setNewRecipientType] = useState('sms');
  const [newRecipientValue, setNewRecipientValue] = useState('');
  const [submittingRecipient, setSubmittingRecipient] = useState(false);

  // IP Whitelist state
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [ipRequests, setIpRequests] = useState([]);
  const [newIpRequest, setNewIpRequest] = useState('');
  const [ipReason, setIpReason] = useState('');
  const [newIpType, setNewIpType] = useState('gui');

  const showToast = (type, msg) => {
    setToast({ show: true, type, message: msg });
    setTimeout(() => setToast({ show: false, type: '', message: '' }), 4000);
  };

  const services = [
    { key: 'messaging', label: 'SMS Messaging', icon: MessageSquare, color: '#4f46e5', enabled: true, desc: 'Bulk SMS, DLT templates, SMPP delivery' },
    { key: 'whatsapp', label: 'WhatsApp Business', icon: MessageCircle, color: '#25D366', enabled: true, desc: 'WhatsApp Business API messaging' },
    { key: 'rcs', label: 'Google RCS', icon: Smartphone, color: '#4285F4', enabled: false, desc: 'Rich Communication Services (Coming Soon)' },
    { key: 'email', label: 'Bulk Email', icon: Mail, color: '#F59E0B', enabled: false, desc: 'Bulk email campaigns (Coming Soon)' },
  ];

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [profileRes, alertRes, ipRes, ipReqRes, contactsRes, recipientReqRes] = await Promise.allSettled([
        api.get('/user/settings/profile'),
        api.get('/user/alerts/config'),
        api.get('/user/settings/ip-whitelist'),
        api.get('/user/settings/ip-requests'),
        api.get('/user/alerts/contacts'),
        userAlertAPI.getMyRecipientRequests(),
      ]);

      if (profileRes.status === 'fulfilled' && profileRes.value.data) {
        setProfile(profileRes.value.data);
      }
      if (alertRes.status === 'fulfilled' && alertRes.value.data) {
        const ad = alertRes.value.data;
        const cfg = ad.config || ad;
        setAlertConfig(prev => ({
          ...prev,
          low_balance_enabled: cfg.low_balance_enabled ?? prev.low_balance_enabled,
          low_balance_threshold: cfg.low_balance_threshold ?? prev.low_balance_threshold,
          login_alert_enabled: cfg.login_alert_enabled ?? prev.login_alert_enabled,
          sms_summary_enabled: cfg.sms_summary_enabled ?? prev.sms_summary_enabled,
          sms_summary_frequency: cfg.sms_summary_frequency || prev.sms_summary_frequency,
          email_enabled: cfg.email_enabled ?? prev.email_enabled,
          sms_enabled: cfg.sms_enabled ?? prev.sms_enabled,
        }));
      }
      if (contactsRes.status === 'fulfilled' && contactsRes.value.data) {
        const cts = contactsRes.value.data?.contacts || [];
        const eList = cts.filter(c => c.contact_type === 'email').map(c => ({ value: c.contact_value, verified: true }));
        const mList = cts.filter(c => c.contact_type === 'sms').map(c => ({ value: c.contact_value, verified: true }));
        setAlertEmails(eList.length > 0 ? eList : [{ value: '', verified: false }]);
        setAlertMobiles(mList.length > 0 ? mList : [{ value: '', verified: false }]);
        setTwoLayerState({});
      }
      if (ipRes.status === 'fulfilled') {
        setIpWhitelist(Array.isArray(ipRes.value.data) ? ipRes.value.data : (ipRes.value.data?.ips || []));
      }
      if (ipReqRes.status === 'fulfilled') {
        setIpRequests(Array.isArray(ipReqRes.value.data) ? ipReqRes.value.data : (ipReqRes.value.data?.requests || []));
      }
      if (recipientReqRes.status === 'fulfilled' && recipientReqRes.value.data) {
        setRecipientRequests(recipientReqRes.value.data.requests || []);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save profile
  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/user/settings/profile', {
        full_name: profile.full_name,
        email: profile.email,
        company_name: profile.company_name,
      });
      showToast('success', 'Profile updated successfully');
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Save alert config
  const saveAlertConfig = async () => {
    setSaving(true);
    try {
      await api.put('/user/alerts/config', {
        low_balance_enabled: alertConfig.low_balance_enabled,
        low_balance_threshold: parseFloat(alertConfig.low_balance_threshold) || 1000,
        login_alert_enabled: alertConfig.login_alert_enabled,
        sms_summary_enabled: alertConfig.sms_summary_enabled,
        sms_summary_frequency: alertConfig.sms_summary_frequency,
        email_enabled: alertConfig.email_enabled,
        sms_enabled: alertConfig.sms_enabled,
      });
      showToast('success', 'Notification preferences saved');
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  // Submit IP whitelist request
  const submitIpRequest = async () => {
    if (!newIpRequest.trim()) {
      showToast('error', 'Please enter an IP address');
      return;
    }
    setSaving(true);
    try {
      await api.post('/user/settings/ip-requests', {
        ip_address: newIpRequest.trim(),
        ip_type: newIpType,
        reason: ipReason.trim() || 'Whitelist request',
      });
      showToast('success', 'IP whitelist request submitted for approval');
      setNewIpRequest('');
      setIpReason('');
      setNewIpType('gui');
      // Refresh IP data
      const [ipRes, ipReqRes] = await Promise.allSettled([
        api.get('/user/settings/ip-whitelist'),
        api.get('/user/settings/ip-requests'),
      ]);
      if (ipRes.status === 'fulfilled') {
        setIpWhitelist(Array.isArray(ipRes.value.data) ? ipRes.value.data : (ipRes.value.data?.ips || []));
      }
      if (ipReqRes.status === 'fulfilled') {
        setIpRequests(Array.isArray(ipReqRes.value.data) ? ipReqRes.value.data : (ipReqRes.value.data?.requests || []));
      }
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to submit IP request');
    } finally {
      setSaving(false);
    }
  };

  // Request IP deletion
  const requestDeleteIp = async (ipAddress, ipType) => {
    if (!window.confirm(`Request removal of IP ${ipAddress} (${ipType.toUpperCase()})? This requires admin approval.`)) return;
    setSaving(true);
    try {
      await api.post('/user/settings/ip-requests', {
        ip_address: ipAddress,
        ip_type: ipType,
        action_type: 'delete',
        reason: 'User requested removal',
      });
      showToast('success', 'IP removal request submitted for approval');
      // Refresh IP data
      const [ipRes, ipReqRes] = await Promise.allSettled([
        api.get('/user/settings/ip-whitelist'),
        api.get('/user/settings/ip-requests'),
      ]);
      if (ipRes.status === 'fulfilled') {
        setIpWhitelist(Array.isArray(ipRes.value.data) ? ipRes.value.data : (ipRes.value.data?.ips || []));
      }
      if (ipReqRes.status === 'fulfilled') {
        setIpRequests(Array.isArray(ipReqRes.value.data) ? ipReqRes.value.data : (ipReqRes.value.data?.requests || []));
      }
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to submit removal request');
    } finally {
      setSaving(false);
    }
  };

  // Regenerate API key
  const regenerateApiKey = async () => {
    if (!window.confirm('Are you sure you want to regenerate your API key? The old key will stop working immediately.')) return;
    setSaving(true);
    try {
      const res = await api.post('/user/settings/regenerate-api-key');
      if (res.data?.api_key) {
        setProfile(prev => ({ ...prev, api_key: res.data.api_key }));
      }
      showToast('success', 'API key regenerated successfully');
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to regenerate API key');
    } finally {
      setSaving(false);
    }
  };

  // Save verified alert contacts
  const saveAlertContacts = async () => {
    const validEmails = alertEmails.filter(e => e.value.trim() && e.verified).map(e => e.value.trim());
    const validMobiles = alertMobiles.filter(m => m.value.trim() && m.verified).map(m => m.value.trim());
    const hasUnverified = alertEmails.some(e => e.value.trim() && !e.verified) || alertMobiles.some(m => m.value.trim() && !m.verified);
    if (hasUnverified) {
      showToast('error', 'Please verify all contacts before saving. Unverified contacts will not be saved.');
    }
    if (validEmails.length === 0 && validMobiles.length === 0 && !hasUnverified) {
      showToast('error', 'Please add at least one contact');
      return;
    }
    setSavingContacts(true);
    try {
      await userAlertAPI.updateContacts({ emails: validEmails, mobiles: validMobiles });
      showToast('success', `Saved ${validEmails.length} email(s) and ${validMobiles.length} mobile(s)`);
    } catch (e) {
      showToast('error', e.response?.data?.error || 'Failed to save contacts');
    } finally {
      setSavingContacts(false);
    }
  };

  // ─── Alert Recipient Request: Submit for admin approval ───
  const handleSubmitRecipientRequest = async () => {
    const val = newRecipientValue.trim();
    if (!val) { showToast('error', 'Enter a contact value'); return; }
    if (newRecipientType === 'email' && !isValidEmail(val)) { showToast('error', 'Enter a valid email address'); return; }
    if (newRecipientType === 'sms' && !isValidMobile(val)) { showToast('error', 'Enter a valid 10-digit mobile number'); return; }
    setSubmittingRecipient(true);
    try {
      const res = await userAlertAPI.submitRecipientRequest({ contact_type: newRecipientType, contact_value: val });
      showToast('success', res.data?.message || 'Request submitted for admin approval');
      setNewRecipientValue('');
      // Refresh requests
      const reqRes = await userAlertAPI.getMyRecipientRequests();
      setRecipientRequests(reqRes.data?.requests || []);
    } catch (e) {
      showToast('error', e.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmittingRecipient(false);
    }
  };

  const handleDeleteRecipientRequest = async (id) => {
    if (!window.confirm('Are you sure you want to remove this contact/request?')) return;
    try {
      await userAlertAPI.deleteRecipientRequest(id);
      showToast('success', 'Request removed');
      const reqRes = await userAlertAPI.getMyRecipientRequests();
      setRecipientRequests(reqRes.data?.requests || []);
    } catch (e) {
      showToast('error', e.response?.data?.error || 'Failed to delete request');
    }
  };

  // DEPRECATED: kept for backward compat but no longer used in UI
  const handleSendPrimaryOTP = async (contactType, idx) => {
    const list = contactType === 'email' ? alertEmails : alertMobiles;
    const contact = list[idx];
    if (!contact?.value.trim()) { showToast('error', `Enter a ${contactType === 'email' ? 'email' : 'mobile'} first`); return; }
    const primaryType = contactType === 'email' ? 'sms' : 'email';
    const key = `${contactType}-${idx}`;
    setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingPrimary: true } }));
    try {
      const res = await userAlertAPI.sendPrimaryOTP({ primary_type: primaryType, new_contact: contact.value.trim() });
      setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingPrimary: false, primaryOtpSent: true, primaryOtp: '', primaryVerified: false, masked: res.data?.masked || '' } }));
      showToast('success', res.data?.message || 'OTP sent to primary contact');
    } catch (e) {
      setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingPrimary: false } }));
      showToast('error', e.response?.data?.error || 'Failed to send primary OTP');
    }
  };

  // ─── 2-Layer OTP: Step 1 — Verify primary OTP ───
  const handleVerifyPrimaryOTP = async (contactType, idx) => {
    const list = contactType === 'email' ? alertEmails : alertMobiles;
    const contact = list[idx];
    const key = `${contactType}-${idx}`;
    const otp = twoLayerState[key]?.primaryOtp;
    if (!otp || otp.length < 4) { showToast('error', 'Enter the OTP'); return; }
    setTwoLayerState(s => ({ ...s, [key]: { ...s[key], verifyingPrimary: true } }));
    try {
      await userAlertAPI.verifyPrimaryOTP({ new_contact: contact.value.trim(), otp });
      setTwoLayerState(s => ({ ...s, [key]: { ...s[key], verifyingPrimary: false, primaryVerified: true, sendingNew: true } }));
      showToast('success', 'Identity verified! Sending OTP to new contact...');
      // Auto-trigger Step 2
      try {
        await userAlertAPI.sendNewContactOTP({ contact_type: contactType === 'email' ? 'email' : 'sms', contact_value: contact.value.trim() });
        setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingNew: false, newOtpSent: true, newOtp: '' } }));
        showToast('success', `OTP sent to ${contact.value}`);
      } catch (e2) {
        setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingNew: false } }));
        showToast('error', e2.response?.data?.error || 'Failed to send OTP to new contact');
      }
    } catch (e) {
      setTwoLayerState(s => ({ ...s, [key]: { ...s[key], verifyingPrimary: false } }));
      showToast('error', e.response?.data?.error || 'Invalid OTP');
    }
  };

  // ─── 2-Layer OTP: Step 2 — Resend OTP to new contact ───
  const handleResendNewContactOTP = async (contactType, idx) => {
    const list = contactType === 'email' ? alertEmails : alertMobiles;
    const contact = list[idx];
    const key = `${contactType}-${idx}`;
    setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingNew: true } }));
    try {
      await userAlertAPI.sendNewContactOTP({ contact_type: contactType === 'email' ? 'email' : 'sms', contact_value: contact.value.trim() });
      setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingNew: false, newOtpSent: true, newOtp: '' } }));
      showToast('success', `OTP resent to ${contact.value}`);
    } catch (e) {
      setTwoLayerState(s => ({ ...s, [key]: { ...s[key], sendingNew: false } }));
      showToast('error', e.response?.data?.error || 'Failed to resend OTP');
    }
  };

  // ─── 2-Layer OTP: Step 2 — Verify new contact OTP ───
  const handleVerifyNewContactOTP = async (contactType, idx) => {
    const list = contactType === 'email' ? alertEmails : alertMobiles;
    const contact = list[idx];
    const key = `${contactType}-${idx}`;
    const otp = twoLayerState[key]?.newOtp;
    if (!otp || otp.length < 4) { showToast('error', 'Enter the OTP'); return; }
    setTwoLayerState(s => ({ ...s, [key]: { ...s[key], verifyingNew: true } }));
    try {
      await userAlertAPI.verifyNewContactOTP({ contact_type: contactType === 'email' ? 'email' : 'sms', contact_value: contact.value.trim(), otp });
      if (contactType === 'email') setAlertEmails(prev => prev.map((e, i) => i === idx ? { ...e, verified: true } : e));
      else setAlertMobiles(prev => prev.map((m, i) => i === idx ? { ...m, verified: true } : m));
      setTwoLayerState(s => { const n = { ...s }; delete n[key]; return n; });
      // Auto-save
      const updatedEmails = contactType === 'email' ? alertEmails.map((e, i) => i === idx ? { ...e, verified: true } : e) : alertEmails;
      const updatedMobiles = contactType === 'mobile' ? alertMobiles.map((m, i) => i === idx ? { ...m, verified: true } : m) : alertMobiles;
      try {
        await userAlertAPI.updateContacts({ emails: updatedEmails.filter(e => e.value.trim() && e.verified).map(e => e.value.trim()), mobiles: updatedMobiles.filter(m => m.value.trim() && m.verified).map(m => m.value.trim()) });
        showToast('success', `${contactType === 'email' ? 'Email' : 'Mobile'} verified & saved (2-layer auth complete)`);
      } catch (e2) { showToast('error', `Verified but save failed: ${e2.response?.data?.error || 'Unknown'}`); }
    } catch (e) {
      setTwoLayerState(s => ({ ...s, [key]: { ...s[key], verifyingNew: false } }));
      showToast('error', e.response?.data?.error || 'Invalid OTP');
    }
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return '₹0';
    return '₹' + Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-3" />
        <p className="text-sm text-gray-500">Loading settings...</p>
      </div>
    );
  }

  // Validation helpers
  const isValidMobile = (val) => /^\d{10}$/.test(val.trim());
  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  // Render a single contact row inline with 2-Layer OTP (NOT a component — avoids remount/focus-loss)
  const renderContact = (item, idx, contactType) => {
    const key = `${contactType}-${idx}`;
    const tl = twoLayerState[key] || {};
    const isMobile = contactType === 'mobile';
    const list = isMobile ? alertMobiles : alertEmails;
    const setList = isMobile ? setAlertMobiles : setAlertEmails;
    const val = item.value.trim();
    const valid = isMobile ? isValidMobile(val) : isValidEmail(val);
    const showHint = val.length > 0 && !valid;

    if (item.verified) {
      return (
        <div key={key} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <span className="flex-1 text-sm font-semibold text-gray-800">{item.value}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700">Verified</span>
          <button onClick={() => { const u = [...list]; u[idx] = { value: item.value, verified: false }; setList(u); setTwoLayerState(s => { const n = { ...s }; delete n[key]; return n; }); }}
            className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all" title="Re-verify"><RotateCcw className="w-3.5 h-3.5" /></button>
          <button onClick={() => { if (list.length <= 1) setList([{ value: '', verified: false }]); else setList(list.filter((_, i) => i !== idx)); }}
            className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><X className="w-3.5 h-3.5" /></button>
        </div>
      );
    }

    return (
      <div key={key} className="space-y-2">
        {/* Input row */}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <input type={isMobile ? 'tel' : 'email'} value={item.value}
              onChange={e => {
                let v = e.target.value;
                if (isMobile) v = v.replace(/\D/g, '').slice(0, 10);
                const u = [...list]; u[idx] = { value: v, verified: false }; setList(u);
                setTwoLayerState(s => { const n = { ...s }; delete n[key]; return n; });
              }}
              placeholder={isMobile ? '9876543210' : 'email@example.com'}
              maxLength={isMobile ? 10 : undefined} className={inputClass} />
            {showHint && <p className="text-[10px] mt-1 text-red-400">{isMobile ? 'Enter exactly 10 digits' : 'Must contain @ and .'}</p>}
          </div>
          {val && valid && !tl.primaryOtpSent && (
            <button onClick={() => handleSendPrimaryOTP(contactType, idx)} disabled={tl.sendingPrimary}
              className="px-5 py-3 text-xs font-bold rounded-xl whitespace-nowrap text-white bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 transition-all flex items-center gap-2">
              {tl.sendingPrimary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify'}
            </button>
          )}
          <button onClick={() => { if (list.length <= 1) setList([{ value: '', verified: false }]); else { setList(list.filter((_, i) => i !== idx)); setTwoLayerState(s => { const n = { ...s }; delete n[key]; return n; }); } }}
            className="p-2.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>

        {/* Step 1: Primary OTP verification (identity check) */}
        {tl.primaryOtpSent && !tl.primaryVerified && (
          <div className="rounded-xl border border-indigo-100 overflow-hidden">
            <div className="px-4 py-2.5 flex items-center gap-2 bg-indigo-50/50 border-b border-indigo-100">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white bg-indigo-500">1</div>
              <span className="text-[11px] font-bold text-indigo-700">Identity Verification</span>
              <span className="text-[10px] text-indigo-400 ml-auto">OTP sent to {tl.masked || 'primary contact'}</span>
            </div>
            <div className="flex gap-2 items-center px-4 py-3 bg-white">
              <KeyRound className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <input type="text" value={tl.primaryOtp || ''} onChange={e => setTwoLayerState(s => ({ ...s, [key]: { ...s[key], primaryOtp: e.target.value.replace(/\D/g, '').slice(0, 6) } }))}
                placeholder="Enter 6-digit OTP" maxLength={6} className="flex-1 bg-transparent text-sm font-mono focus:outline-none text-gray-800 placeholder-gray-400" />
              <button onClick={() => handleVerifyPrimaryOTP(contactType, idx)} disabled={tl.verifyingPrimary || !tl.primaryOtp || tl.primaryOtp.length < 4}
                className="px-4 py-2 text-xs font-bold text-white rounded-lg bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 transition-all">
                {tl.verifyingPrimary ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify'}
              </button>
              <button onClick={() => handleSendPrimaryOTP(contactType, idx)} className="text-[10px] font-bold text-indigo-500 hover:underline whitespace-nowrap">Resend</button>
            </div>
            <div className="px-4 py-1.5 flex items-center gap-1.5 bg-gray-50/80 border-t border-gray-100">
              <Clock className="w-3 h-3 text-gray-400" />
              <span className="text-[9px] text-gray-400">OTP expires in 10 minutes</span>
            </div>
          </div>
        )}

        {/* Step 2: New contact OTP verification */}
        {tl.primaryVerified && tl.newOtpSent && (
          <div className="rounded-xl border border-emerald-100 overflow-hidden">
            <div className="px-4 py-2.5 flex items-center gap-2 bg-emerald-50/50 border-b border-emerald-100">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white bg-emerald-500">2</div>
              <span className="text-[11px] font-bold text-emerald-700">Verify New {isMobile ? 'Mobile' : 'Email'}</span>
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400 ml-auto" />
              <span className="text-[10px] text-emerald-500">Step 1 Done</span>
            </div>
            <div className="flex gap-2 items-center px-4 py-3 bg-white">
              <KeyRound className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <input type="text" value={tl.newOtp || ''} onChange={e => setTwoLayerState(s => ({ ...s, [key]: { ...s[key], newOtp: e.target.value.replace(/\D/g, '').slice(0, 6) } }))}
                placeholder="Enter 6-digit OTP" maxLength={6} className="flex-1 bg-transparent text-sm font-mono focus:outline-none text-gray-800 placeholder-gray-400" />
              <button onClick={() => handleVerifyNewContactOTP(contactType, idx)} disabled={tl.verifyingNew || !tl.newOtp || tl.newOtp.length < 4}
                className="px-4 py-2 text-xs font-bold text-white rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 transition-all">
                {tl.verifyingNew ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify & Save'}
              </button>
              <button onClick={() => handleResendNewContactOTP(contactType, idx)} className="text-[10px] font-bold text-emerald-500 hover:underline whitespace-nowrap">Resend</button>
            </div>
          </div>
        )}

        {/* Loading between step 1 and step 2 */}
        {tl.primaryVerified && !tl.newOtpSent && tl.sendingNew && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" />
            <span className="text-[11px] text-emerald-600">Sending OTP to new contact...</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 md:p-6">
      {/* Toast */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold flex items-center gap-2 ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
          {toast.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast.message}
          <button onClick={() => setToast({ show: false, type: '', message: '' })} className="ml-2 hover:opacity-80"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Header — matches KafkaSMSPipeline */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
              <Settings className="w-6 h-6 text-white" />
            </div>
            Account Settings
          </h1>
          <p className="text-sm text-gray-400 mt-1">Manage your profile, alerts, security & billing</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-4 py-2.5">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-bold text-gray-700">{profile.username}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-700">{profile.role?.replace('_', ' ').toUpperCase() || 'USER'}</span>
          </div>
        </div>
      </div>

      {/* Tabs — matches KafkaSMSPipeline */}
      <div className="flex gap-1 bg-white rounded-2xl border border-gray-100 p-1.5 mb-6 shadow-sm overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-indigo-500 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        {saving && <Loader2 className="w-4 h-4 text-indigo-400 animate-spin ml-auto self-center mr-3" />}
      </div>

      {/* ═══════════════ GENERAL ═══════════════ */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Profile Information */}
              <Card>
                <CardHeader icon={UserCircle} title="Profile Information" subtitle="Update your personal details" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Username</Label>
                    <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-gray-50 border border-gray-100">
                      <AtSign className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="text-sm font-semibold text-gray-700">{profile.username || '—'}</span>
                    </div>
                  </div>
                  <div>
                    <Label>Mobile</Label>
                    <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-gray-50 border border-gray-100">
                      <Phone className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="text-sm font-semibold text-gray-700">{profile.mobile || '—'}</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div><Label>Full Name</Label><input type="text" value={profile.full_name || ''} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))} className={inputClass} placeholder="Your full name" /></div>
                  <div><Label>Email</Label><input type="email" value={profile.email || ''} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} className={inputClass} placeholder="you@company.com" /></div>
                </div>
                <div className="mt-4"><Label>Company</Label><input type="text" value={profile.company_name || ''} onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))} className={inputClass} placeholder="Company name" /></div>
                <div className="flex justify-end mt-5">
                  <button onClick={saveProfile} disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 disabled:opacity-40 transition-all">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Profile
                  </button>
                </div>
              </Card>

              {/* API Key */}
              <Card>
                <CardHeader icon={Key} title="API Key" subtitle="Use this key for API authentication" />
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2.5 px-4 py-3 rounded-xl font-mono text-xs text-gray-600 bg-gray-50 border border-gray-100 overflow-hidden">
                    <Hash className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                    <span className="truncate">{profile.api_key || 'No API key generated'}</span>
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(profile.api_key || ''); showToast('success', 'Copied!'); }}
                    className="p-3 rounded-xl bg-indigo-50 text-indigo-500 hover:bg-indigo-100 transition-all" title="Copy"><Copy className="w-4 h-4" /></button>
                  <button onClick={regenerateApiKey} disabled={saving}
                    className="p-3 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-all" title="Regenerate"><RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} /></button>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Services */}
              <Card>
                <CardHeader icon={Layers} title="Services" />
                <div className="space-y-2">
                  {services.map((svc) => {
                    const SvcIcon = svc.icon;
                    return (
                      <div key={svc.key} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-gray-50">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${svc.color}12`, color: svc.color }}><SvcIcon className="w-4 h-4" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-gray-800 truncate">{svc.label}</p>
                          <p className="text-[10px] text-gray-400 truncate">{svc.desc}</p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${svc.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {svc.enabled ? 'Active' : 'Soon'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader icon={BarChart3} title="Quick Stats" />
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs text-gray-500">Balance</span>
                    </div>
                    <span className="text-sm font-bold text-emerald-600">{formatCurrency(profile.balance)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Member Since</span>
                    </div>
                    <span className="text-sm font-bold text-gray-700">{profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ NOTIFICATIONS ═══════════════ */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 space-y-6">
              {/* Alert Types */}
              <Card>
                <CardHeader icon={Bell} title="Alert Types" subtitle="Select which alerts you want to receive" />
                <div className="space-y-3">
                  {[
                    { key: 'low_balance_enabled', icon: Wallet, color: '#ef4444', title: 'Low Balance Alert', desc: 'When balance drops below threshold', hasExtra: true },
                    { key: 'login_alert_enabled', icon: ShieldAlert, color: '#f59e0b', title: 'Login Alert', desc: 'New login from unknown IP or device' },
                    { key: 'sms_summary_enabled', icon: FileBarChart, color: '#06b6d4', title: 'SMS Summary Report', desc: 'Periodic delivery summary', hasFreq: true },
                  ].map((alert) => {
                    const AlertIcon = alert.icon;
                    return (
                      <div key={alert.key}>
                        <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-all"
                          style={{ background: alertConfig[alert.key] ? `${alert.color}06` : '#fafbfc' }}>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg" style={{ background: `${alert.color}12`, color: alert.color }}><AlertIcon className="w-4 h-4" /></div>
                            <div><p className="text-sm font-bold text-gray-800">{alert.title}</p><p className="text-[11px] text-gray-400">{alert.desc}</p></div>
                          </div>
                          <Toggle enabled={alertConfig[alert.key]} onChange={v => setAlertConfig(p => ({ ...p, [alert.key]: v }))} />
                        </div>
                        {alert.hasExtra && alertConfig.low_balance_enabled && (
                          <div className="ml-14 mt-2 flex items-center gap-2">
                            <span className="text-xs text-gray-400">Threshold:</span>
                            <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white border border-gray-200">
                              <span className="text-sm font-bold text-gray-300">₹</span>
                              <input type="number" value={alertConfig.low_balance_threshold || ''} onChange={e => setAlertConfig(p => ({ ...p, low_balance_threshold: e.target.value }))} className="w-20 text-sm font-semibold text-gray-800 focus:outline-none bg-transparent" />
                            </div>
                          </div>
                        )}
                        {alert.hasFreq && alertConfig.sms_summary_enabled && (
                          <div className="ml-14 mt-2 flex items-center gap-2">
                            <span className="text-xs text-gray-400">Frequency:</span>
                            <select value={alertConfig.sms_summary_frequency || 'daily'} onChange={e => setAlertConfig(p => ({ ...p, sms_summary_frequency: e.target.value }))} className="text-xs font-semibold text-gray-700 px-3 py-2 rounded-lg bg-white border border-gray-200 focus:outline-none">
                              <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Delivery Channels */}
              <Card>
                <CardHeader icon={Send} title="Delivery Channels" subtitle="How do you want to receive alerts?" />
                <div className="grid grid-cols-2 gap-3">
                  {[{ key: 'email_enabled', icon: Mail, label: 'Email' }, { key: 'sms_enabled', icon: Smartphone, label: 'SMS' }].map(ch => {
                    const ChIcon = ch.icon;
                    return (
                      <button key={ch.key} onClick={() => setAlertConfig(p => ({ ...p, [ch.key]: !p[ch.key] }))}
                        className={`flex flex-col items-center gap-2.5 p-5 rounded-2xl border-2 transition-all duration-200 hover:shadow-md ${alertConfig[ch.key] ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200 bg-white'}`}>
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${alertConfig[ch.key] ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}><ChIcon className="w-5 h-5" /></div>
                        <span className={`text-xs font-bold ${alertConfig[ch.key] ? 'text-indigo-600' : 'text-gray-400'}`}>{ch.label}</span>
                        {alertConfig[ch.key] && <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-end mt-5">
                  <button onClick={saveAlertConfig} disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 disabled:opacity-40 transition-all">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Preferences
                  </button>
                </div>
              </Card>
            </div>

            {/* Alert Recipients — Master Admin Approval Workflow */}
            <div className="lg:col-span-2">
              <div className="sticky top-4 space-y-4">
                {/* Add New Recipient */}
                <Card>
                  <CardHeader icon={ShieldCheck} title="Alert Recipients" subtitle="Add contacts for login alerts (requires admin approval)" />

                  <div className="flex items-start gap-2.5 p-3 rounded-xl mb-4 bg-indigo-50/50 border border-indigo-100">
                    <ShieldCheck className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[11px] font-bold text-indigo-700 mb-1">Admin Approval Required</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {['Add contact', 'Pending approval', 'Admin approves'].map((step, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <ChevronRight className="w-3 h-3 text-indigo-200 flex-shrink-0" />}
                            <div className="flex items-center gap-1">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0 ${i === 2 ? 'bg-emerald-500' : 'bg-indigo-500'}`}>{i + 1}</div>
                              <span className="text-[10px] text-indigo-600 whitespace-nowrap">{step}</span>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                      <p className="text-[9px] text-indigo-400 mt-1.5 flex items-center gap-1"><Clock className="w-3 h-3" />Contacts become active only after Master Admin approval.</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <select value={newRecipientType} onChange={e => { setNewRecipientType(e.target.value); setNewRecipientValue(''); }}
                        className="px-3 py-3 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white">
                        <option value="sms">Mobile</option>
                        <option value="email">Email</option>
                      </select>
                      <input type={newRecipientType === 'sms' ? 'tel' : 'email'}
                        value={newRecipientValue}
                        onChange={e => {
                          let v = e.target.value;
                          if (newRecipientType === 'sms') v = v.replace(/\D/g, '').slice(0, 10);
                          setNewRecipientValue(v);
                        }}
                        placeholder={newRecipientType === 'sms' ? '9876543210' : 'email@example.com'}
                        maxLength={newRecipientType === 'sms' ? 10 : undefined}
                        className={`${inputClass} flex-1`} />
                      <button onClick={handleSubmitRecipientRequest} disabled={submittingRecipient || !newRecipientValue.trim()}
                        className="px-5 py-3 text-xs font-bold rounded-xl whitespace-nowrap text-white bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 transition-all flex items-center gap-2">
                        {submittingRecipient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Submit
                      </button>
                    </div>
                  </div>
                </Card>

                {/* Recipient Requests Grid */}
                {recipientRequests.length > 0 && (
                  <Card>
                    <CardHeader icon={BellRing} title="Alert Recipients Grid"
                      badge={`${recipientRequests.filter(r => r.status === 'approved' && r.is_active).length} active`} />
                    <div className="overflow-x-auto -mx-5 px-5">
                      <table className="w-full text-xs min-w-[600px]">
                        <thead>
                          <tr className="bg-gray-50/80">
                            <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Request #</th>
                            <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Type</th>
                            <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Contact</th>
                            <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                            <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Requested</th>
                            <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Action Date</th>
                            <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Remarks</th>
                            <th className="text-center py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {recipientRequests.map((req) => (
                            <tr key={req.id} className={`transition-colors ${req.status === 'deleted' ? 'bg-gray-50/50 opacity-60' : 'hover:bg-indigo-50/30'}`}>
                              <td className="py-2.5 px-3 font-mono text-[10px] text-indigo-600 font-bold">{req.request_number}</td>
                              <td className="py-2.5 px-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${req.contact_type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                  {req.contact_type === 'email' ? <Mail className="w-2.5 h-2.5" /> : <Phone className="w-2.5 h-2.5" />}
                                  {req.contact_type === 'email' ? 'Email' : 'Mobile'}
                                </span>
                              </td>
                              <td className={`py-2.5 px-3 font-mono font-semibold text-[11px] ${req.status === 'deleted' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{req.contact_value}</td>
                              <td className="py-2.5 px-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                  req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                  req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  req.status === 'deleted' ? 'bg-gray-200 text-gray-600' :
                                  'bg-amber-100 text-amber-700'
                                }`}>{req.status === 'approved' ? 'Approved' : req.status === 'rejected' ? 'Rejected' : req.status === 'deleted' ? 'Deleted by Admin' : 'Pending Approval'}</span>
                              </td>
                              <td className="py-2.5 px-3 text-gray-400 whitespace-nowrap text-[10px]">{formatDate(req.requested_at)}</td>
                              <td className="py-2.5 px-3 text-gray-400 whitespace-nowrap text-[10px]">{req.actioned_at ? formatDate(req.actioned_at) : '—'}</td>
                              <td className="py-2.5 px-3 text-gray-400 max-w-[120px] truncate text-[10px]" title={req.admin_remarks || ''}>{req.admin_remarks || '—'}</td>
                              <td className="py-2.5 px-3 text-center">
                                {req.status === 'pending' && (
                                  <button onClick={() => handleDeleteRecipientRequest(req.id)}
                                    className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all" title="Cancel request">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ SECURITY ═══════════════ */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 space-y-6">
              {/* Whitelisted IPs */}
              <Card>
                <CardHeader icon={ShieldCheck} title="Whitelisted IPs" badge={`${ipWhitelist.length} active`} />
                {ipWhitelist.length > 0 ? (
                  <div className="space-y-2">
                    {ipWhitelist.map((ip, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 hover:bg-gray-100/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 6px rgba(16,185,129,0.4)' }} />
                          <span className="text-sm font-mono font-semibold text-gray-800">{typeof ip === 'string' ? ip : ip.ip_address}</span>
                          {ip.ip_type && <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${ip.ip_type === 'gui' ? 'bg-indigo-100 text-indigo-700' : ip.ip_type === 'smpp' ? 'bg-cyan-100 text-cyan-700' : 'bg-amber-100 text-amber-700'}`}>{ip.ip_type === 'gui' ? 'GUI' : ip.ip_type === 'smpp' ? 'SMPP' : 'API'}</span>}
                          {ip.created_at && <span className="text-[10px] text-gray-400">{formatDate(ip.created_at)}</span>}
                        </div>
                        <button onClick={() => requestDeleteIp(ip.ip_address, ip.ip_type)} disabled={saving} className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <Globe className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No IPs whitelisted</p>
                    <p className="text-[11px] text-gray-300 mt-0.5">All IPs are currently allowed</p>
                  </div>
                )}
              </Card>

              {/* Request History */}
              {ipRequests.length > 0 && (
                <Card>
                  <CardHeader icon={History} title="Request History" badge={`${ipRequests.length} requests`} />
                  <div className="overflow-x-auto -mx-5 px-5">
                    <table className="w-full text-xs min-w-[700px]">
                      <thead>
                        <tr className="bg-gray-50/80">
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Request #</th>
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">IP Address</th>
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Type</th>
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Action</th>
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Requested</th>
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Approved</th>
                          <th className="text-left py-3 px-3 text-[10px] font-semibold text-gray-500 uppercase">Remarks</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {ipRequests.map((req, i) => (
                          <tr key={i} className="hover:bg-indigo-50/30 transition-colors">
                            <td className="py-2.5 px-3 font-mono text-[10px] text-indigo-600 font-bold">{req.request_number || `#${req.id}`}</td>
                            <td className="py-2.5 px-3 font-mono font-semibold text-gray-800">{req.ip_address}</td>
                            <td className="py-2.5 px-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${req.ip_type === 'gui' ? 'bg-indigo-100 text-indigo-700' : req.ip_type === 'smpp' ? 'bg-cyan-100 text-cyan-700' : 'bg-amber-100 text-amber-700'}`}>{req.ip_type?.toUpperCase() || '—'}</span></td>
                            <td className="py-2.5 px-3 text-gray-600 capitalize">{req.action_type || 'add'}</td>
                            <td className="py-2.5 px-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : req.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{req.status}</span></td>
                            <td className="py-2.5 px-3 text-gray-400 whitespace-nowrap">{formatDate(req.created_at)}</td>
                            <td className="py-2.5 px-3 text-gray-400 whitespace-nowrap">{req.approved_at ? formatDate(req.approved_at) : '—'}</td>
                            <td className="py-2.5 px-3 text-gray-400 max-w-[120px] truncate" title={req.remarks || ''}>{req.remarks || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>

            {/* Request New IP */}
            <div className="lg:col-span-2"><div className="sticky top-4">
              <Card>
                <CardHeader icon={PlusCircle} title="Request New IP" subtitle="Submit for admin approval" />
                <div className="space-y-4">
                  <div><Label>IP Address</Label><input type="text" value={newIpRequest} onChange={e => setNewIpRequest(e.target.value)} className={inputClass} placeholder="e.g. 103.21.58.100" /></div>
                  <div>
                    <Label>Access Type</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {[{ val: 'gui', label: 'GUI', icon: Monitor, color: '#4f46e5' }, { val: 'api', label: 'HTTP API', icon: Code, color: '#f59e0b' }, { val: 'smpp', label: 'SMPP', icon: Radio, color: '#06b6d4' }].map(t => {
                        const TIcon = t.icon;
                        return (
                          <button key={t.val} onClick={() => setNewIpType(t.val)}
                            className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-200 ${newIpType === t.val ? 'shadow-sm' : ''}`}
                            style={{ borderColor: newIpType === t.val ? t.color : '#e2e8f0', background: newIpType === t.val ? `${t.color}08` : '#fff', color: newIpType === t.val ? t.color : '#94a3b8' }}>
                            <TIcon className="w-4 h-4" /><span className="text-[10px] font-bold">{t.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div><Label>Reason</Label><input type="text" value={ipReason} onChange={e => setIpReason(e.target.value)} className={inputClass} placeholder="e.g. Office network" /></div>
                  <button onClick={submitIpRequest} disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 disabled:opacity-40 transition-all">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for Approval
                  </button>
                </div>
              </Card>
            </div></div>
          </div>
        </div>
      )}

      {/* ═══════════════ BILLING ═══════════════ */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Wallet, label: 'Balance', value: formatCurrency(profile.balance), color: 'emerald' },
              { icon: Key, label: 'API Key', value: profile.api_key ? 'Active' : 'None', color: profile.api_key ? 'emerald' : 'red' },
              { icon: Calendar, label: 'Member Since', value: profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—', color: 'indigo' },
            ].map((c, i) => {
              const CIcon = c.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-2 rounded-lg bg-${c.color}-50`}><CIcon className={`w-4 h-4 text-${c.color}-500`} /></div>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{c.label}</span>
                  </div>
                  <div className="text-xl font-bold text-gray-900">{c.value}</div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              {/* Low Balance Alert */}
              <Card>
                <CardHeader icon={BellRing} title="Low Balance Alert" subtitle="Get notified when balance is low" />
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-all"
                  style={{ background: alertConfig.low_balance_enabled ? 'rgba(239,68,68,0.03)' : '#fafbfc' }}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-50"><AlertTriangle className="w-4 h-4 text-red-500" /></div>
                    <div><p className="text-sm font-bold text-gray-800">Enable alert</p><p className="text-[11px] text-gray-400">Notify when balance drops</p></div>
                  </div>
                  <Toggle enabled={alertConfig.low_balance_enabled} onChange={v => setAlertConfig(p => ({ ...p, low_balance_enabled: v }))} />
                </div>
                {alertConfig.low_balance_enabled && (
                  <div className="ml-14 mt-3 flex items-center gap-2">
                    <span className="text-xs text-gray-400">Alert below:</span>
                    <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white border border-gray-200">
                      <span className="text-sm font-bold text-gray-300">₹</span>
                      <input type="number" value={alertConfig.low_balance_threshold || ''} onChange={e => setAlertConfig(p => ({ ...p, low_balance_threshold: e.target.value }))} className="w-24 text-sm font-semibold text-gray-800 focus:outline-none bg-transparent" />
                    </div>
                  </div>
                )}
                <div className="flex justify-end mt-5">
                  <button onClick={saveAlertConfig} disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl text-sm font-bold hover:bg-indigo-600 disabled:opacity-40 transition-all">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                  </button>
                </div>
              </Card>
            </div>

            {/* Account Details */}
            <Card>
              <CardHeader icon={Receipt} title="Account Details" />
              <div className="space-y-0">
                {[
                  { label: 'Username', value: profile.username }, { label: 'Role', value: profile.role?.replace('_', ' ').toUpperCase() },
                  { label: 'Email', value: profile.email }, { label: 'Mobile', value: profile.mobile },
                  { label: 'Company', value: profile.company_name },
                  { label: 'Member Since', value: profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                    <span className="text-xs text-gray-400">{item.label}</span>
                    <span className="text-xs font-semibold text-gray-700 text-right max-w-[60%] truncate">{item.value || '—'}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSettingsPage;
